<script lang="ts">
	import { type Achievement, AchievementProgressState } from '../../domain/Achievement';
	import { onMount } from 'svelte';
	import { findAllAchievementResponse } from '../../store/AchievementStore';
	import { Topic } from '$services/tracking/Topic';
	import { ActionName } from '$services/tracking/TrackingService';
	import { onViewport } from '$services/tracking/IntersectionObserver';
	import AchievementTrackingService from '$services/tracking/AchievementTrackingService';
	import AchievementActivatedCard from '$components/home/achievement/AchievementActivatedCard.svelte';
	import AchievementCreatedCard from '$components/home/achievement/AchievementCreatedCard.svelte';
	import AchievementComingSoonCard from '$components/home/achievement/AchievementComingSoonCard.svelte';

	const MOBILE_BREAKPOINT = 432;
	let achievementsToShow: Achievement[] = $state([]);
	let isMobile: boolean = $state(false);
	let cinemaCardsStyle = $derived.by(() => {
		if (!isMobile) {
			return achievementsToShow?.length > 3 ? 'achievement-cinema__above_three_cards' : 'achievement-cinema__three_or_less_cards';
		}
		return '';
	});
	let showHorizontalCard = $derived.by(() => {
		return !isMobile && achievementsToShow.length === 1;
	});
	let cut = $derived(isMobile ? '0.1' : '0.5');
	let achievementTrackingService: AchievementTrackingService;
	let cinemaElement: HTMLElement;
	const trackedAchievements: string[] = [];

	const trackClick = (index: number) => {
		achievementTrackingService.sendAchievementClickFeatureTracking(
			achievementsToShow[index],
			index + 1,
			cinemaElement
		);
	};

	const trackView = () => {
		const interval = setInterval(() => {
			//@ts-ignore
			if (cinemaElement.visibleItems.length > 0) {
				let elements : HTMLElement[] = [];
				elements.push(cinemaElement);
				//@ts-ignore
				cinemaElement.visibleItems.forEach((visibleItem) => {
					elements.push(visibleItem);
					trackedAchievements.push(visibleItem.getAttribute('data-achievement-id'));
				});
				achievementTrackingService.sendVisibleAchievementsFeatureTracking(elements);
				clearInterval(interval);
			}
		}, 100);

		setTimeout(() => {
			clearInterval(interval);
		}, 5000);
	};

	const fetchAllAchievements = () => {
		let actualAchievements: Achievement[] = findAllAchievementResponse().actualAchievements;
		let comingSoonAchievements: Achievement[] = findAllAchievementResponse().comingSoonAchievements;
		return actualAchievements.concat(comingSoonAchievements);
	};

	const handleAchievementChange = () => {
		achievementsToShow = fetchAllAchievements();
	};

	//@ts-ignore
	const handleInstructionsSheetClose = (event) => {
		if (event.detail.id === 'created-instructions-sheet') {
			achievementsToShow = fetchAllAchievements();
		}
	};

	onMount(() => {
		isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
		achievementsToShow = fetchAllAchievements();
		achievementTrackingService = new AchievementTrackingService();
		achievementTrackingService.sendAchievementsCinemaFeatureTracking(
			Topic.AddFeaturesToPageImpression,
			'loaded',
			achievementsToShow,
			null,
			cinemaElement
		);

		cinemaElement.addEventListener('oc-item-visibility-changed', (event) => {
			//@ts-ignore
			if (event.detail.scrollDirection === 'right') {
				//@ts-ignore
				event.detail.visibleItems.elements.forEach((achievementBaseCard: HTMLElement) => {
					const achievementToTrack = Number(achievementBaseCard.children[0].id.split('-')[2]);
					const achievement = achievementsToShow[achievementToTrack - 1];
					if (trackedAchievements.includes(achievement.achievementId)) {
						return;
					}
					achievementTrackingService.sendAchievementsCinemaFeatureTracking(
						Topic.AddActionToEvent,
						'visible',
						[achievement],
						ActionName.NEXT,
						cinemaElement,
						achievementToTrack
					);
					trackedAchievements.push(achievement.achievementId);
				});
			}
		});

		document.addEventListener('oc-close', handleInstructionsSheetClose);
	});

</script>

<div
	data-testid="achievements-cinema-container"
	on:enterViewport={trackView}
	use:onViewport
>
	<oc-cinema-v1
		bind:this={cinemaElement}
		class="achievement-cinema oc-pt-75 {cinemaCardsStyle}"
		cut={cut}
		data-testid="new-achievements-cinema"
		data-feature-id="Achievement_Container"
		item-visibility-threshold="0.05"
		items-per-page={isMobile ? '1' : '3'}
		oc-aria-label=""
		resizing="stretch-cut"
	>
		{#each achievementsToShow as achievement, i}
			{#if achievement.progress.state === AchievementProgressState.ACTIVATED}
				<AchievementActivatedCard
					index={i}
					achievement={achievement}
					isHorizontal={showHorizontalCard}
					onclick={() => trackClick(i)}
					display="row"
				/>
			{:else if achievement.progress.state === AchievementProgressState.CREATED }
				<AchievementCreatedCard
					index={i}
					achievement={achievement}
					isHorizontal={showHorizontalCard}
					onclick={() => trackClick(i)}
					on:achievementActivated={() => handleAchievementChange()}
					display="row"
				/>
			{:else if achievement.progress.state === AchievementProgressState.COMING_SOON}
				<AchievementComingSoonCard
					index={i}
					achievement={achievement}
					isHorizontal={showHorizontalCard}
					onclick={() => trackClick(i)}
					display="row"
				/>
			{/if}
		{/each}
	</oc-cinema-v1>
</div>

<style lang="scss">
  @use '../../../node_modules/@otto-ec/otto-components-utils/scss/mixins' as mixins;

  .achievement-cinema {
    &__above_three_cards {
      @include mixins.breakpoint(mixins.$breakpoint-l) {
        :global(> *) {
          width: 296px;
        }
      }

      :global(> *) {
        min-width: 296px;
      }
    }

    &__three_or_less_cards {
      :global(> *) {
        flex: 1;
      }
    }
  }

</style>
